import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  Card,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';
import { radius } from '@karehero/llama/theme/radius';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import StackPage from '@/karehero-app/shell/StackPage';
import {
  COMPLETE,
  INTRO,
  NAME_UNKNOWN,
  PREFILL,
  SECTIONS,
  STEPS,
  type Step,
} from '@/karehero-app/care/forms/FinancialAssessment.content';

// A single answer: a plain string for most fields, a list for multi-selects,
// or a list of rows for a repeatable table (e.g. illnesses).
export type IllnessRow = Record<string, string>;
export type AnswerValue = string | string[] | IllnessRow[];
export type Answers = Record<string, AnswerValue>;

// Typed reads off the loose answer bag — each field kind knows the shape it
// stored, so it pulls the matching view and ignores anything else.
const asText = (v: AnswerValue | undefined): string =>
  typeof v === 'string' ? v : '';
const asList = (v: AnswerValue | undefined): string[] =>
  Array.isArray(v) ? (v.filter((x) => typeof x === 'string') as string[]) : [];
const asRows = (v: AnswerValue | undefined): IllnessRow[] =>
  Array.isArray(v)
    ? (v.filter((x) => x && typeof x === 'object') as IllnessRow[])
    : [];

// ── Molecules ────────────────────────────────────────────────────────────────

// Tappable option card with a radio dot — the onboarding OptionCard pattern.
// The surface is llama's `option` / `optionSelected` variant; only the dot is
// local, because this form draws its own rather than using a Radio.
const ChoiceCard = ({
  selected,
  label,
  onClick,
}: {
  selected: boolean;
  label: string;
  onClick: () => void;
}) => (
  <Card variant={selected ? 'optionSelected' : 'option'} onClick={onClick}>
    <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          flexShrink: 0,
          border: 2,
          borderColor: selected ? 'primary.main' : 'custom.neutral.300',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {selected && (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: 'primary.main',
            }}
          />
        )}
      </Box>
      <Typography variant='h6'>{label}</Typography>
    </Stack>
  </Card>
);

// The flow's primary action — a full-width button for the StackPage footer.
const PrimaryAction = ({
  label,
  onPress,
}: {
  label: string;
  onPress: () => void;
}) => (
  <Button
    variant='primary'
    size='lg'
    isFullWidth
    ariaLabel={label}
    onPress={onPress}
  >
    {label}
  </Button>
);

// ── Helpers ──────────────────────────────────────────────────────────────────

const matches = (step: Step, a: Answers) =>
  !step.showIf || step.showIf.in.includes(asText(a[step.showIf.key]));

const sectionTitle = (id: string) =>
  SECTIONS.find((s) => s.id === id)?.title ?? '';

const groupDigits = (raw: string) => {
  const digits = raw.replace(/[^\d]/g, '');
  return digits ? Number(digits).toLocaleString('en-GB') : '';
};

const optionLabel = (step: Step, value: string) =>
  step.options?.find((o) => o.value === value)?.label ?? value;

// One answer, formatted for the review list.
const formatValue = (step: Step, a: Answers): string => {
  if (step.kind === 'name') {
    return [asText(a.firstName), asText(a.lastName)].filter(Boolean).join(' ');
  }
  if (step.kind === 'multiselect') {
    return asList(a[step.key])
      .map((v) => optionLabel(step, v))
      .join(', ');
  }
  if (step.kind === 'table') {
    // Summarise the rows by their first column (the illness name).
    const first = step.columns?.[0]?.key ?? '';
    const names = asRows(a[step.key])
      .map((r) => r[first])
      .filter(Boolean);
    return names.join(', ');
  }
  const v = asText(a[step.key]);
  if (!v) return '';
  if (step.kind === 'currency') {
    const period = step.periods
      ? asText(a[`${step.key}Period`]) || step.per
      : step.per;
    return `£${groupDigits(v)}${period ? ` ${period}` : ''}`;
  }
  return optionLabel(step, v);
};

// ── Field renderer ───────────────────────────────────────────────────────────

type FieldChildProps = {
  step: Step;
  a: Answers;
  set: (patch: Answers) => void;
};

// A shrunk-label text field, the shared look for form and table inputs.
const textFieldProps = {
  fullWidth: true,
  slotProps: { inputLabel: { shrink: true } },
} as const;

// The dropdown menu portals out of the phone frame and won't inherit the
// field's width, so cap it to the screen. With whiteSpace:'normal' items, long
// options wrap instead of stretching the menu past the frame edge.
const selectProps = {
  MenuProps: { slotProps: { paper: { sx: { maxWidth: 327 } } } },
} as const;

// A repeatable group: one card per entry, each holding the step's `columns`,
// with add/remove. Columns are text or select; values are strings so a row is
// a plain record. One entry is open in an edit form at a time; the rest
// collapse to a read-only summary with Edit / Remove, so adding another tucks
// the finished ones away rather than stacking full forms.
const colText = (col: Step, row: IllnessRow) =>
  col.kind === 'select' ? optionLabel(col, asText(row[col.key])) : row[col.key];

// A bin, pinned top-right of a card, to remove that entry. Stops the click from
// bubbling to the card (which would open the editor).
const RemoveBin = ({ onRemove }: { onRemove: () => void }) => (
  <IconButton
    aria-label='Remove this entry'
    onClick={(e) => {
      e.stopPropagation();
      onRemove();
    }}
    size='small'
    sx={{ mt: -0.5, mr: -0.5, color: 'text.secondary' }}
  >
    <Icon icon={'trash' as never} fontSize={16} />
  </IconButton>
);

// A finished entry, collapsed: its fields as label/value lines, with a bin
// top-right to remove it. Tapping the card reopens it in the editor.
const IllnessSummary = ({
  cols,
  row,
  onEdit,
  onRemove,
}: {
  cols: Step[];
  row: IllnessRow;
  onEdit: () => void;
  onRemove: () => void;
}) => {
  return (
    <Card variant='listItem' onClick={onEdit} sx={{ p: 2 }}>
      <Stack sx={{ alignItems: 'flex-start' }} direction='row' spacing={1}>
        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={1.5}>
          {cols.map((col) => {
            const value = colText(col, row);
            if (!value) return null;
            return (
              <Box key={col.key}>
                <Typography variant='body2' color='text.secondary'>
                  {col.label ?? col.question}
                </Typography>
                <Typography variant='body1'>{value}</Typography>
              </Box>
            );
          })}
        </Stack>
        <RemoveBin onRemove={onRemove} />
      </Stack>
    </Card>
  );
};

const RepeatableTable = ({ step, a, set }: FieldChildProps) => {
  const cols = step.columns ?? [];
  const rows = asRows(a[step.key]);
  // The first column is the name — an entry can't be saved without one.
  const nameKey = cols[0]?.key ?? '';
  const hasName = (row: IllnessRow) => Boolean(asText(row[nameKey]).trim());
  const noun = step.itemLabel ?? 'entry';

  // The entry being added/edited in the bottom sheet, held as a draft so
  // closing the sheet discards partial input. index=null means adding new.
  const [draft, setDraft] = useState<IllnessRow | null>(null);
  const [draftIndex, setDraftIndex] = useState<number | null>(null);

  const openAdd = () => {
    setDraft({});
    setDraftIndex(null);
  };
  const openEdit = (i: number) => {
    setDraft({ ...rows[i] });
    setDraftIndex(i);
  };
  const closeSheet = () => setDraft(null);
  const commitDraft = () => {
    if (!draft) return;
    set({
      [step.key]:
        draftIndex === null
          ? [...rows, draft]
          : rows.map((r, i) => (i === draftIndex ? draft : r)),
    });
  };
  const removeRow = (i: number) =>
    set({ [step.key]: rows.filter((_, ri) => ri !== i) });
  const isNew = draftIndex === null;

  return (
    <Stack spacing={2}>
      {rows.map((row, i) => (
        <IllnessSummary
          key={i}
          cols={cols}
          row={row}
          onEdit={() => openEdit(i)}
          onRemove={() => removeRow(i)}
        />
      ))}
      <Button
        variant='secondary'
        size='md'
        isFullWidth
        ariaLabel={rows.length ? (step.addLabel ?? 'Add another') : 'Add'}
        onPress={openAdd}
      >
        {rows.length ? (step.addLabel ?? 'Add another') : 'Add'}
      </Button>

      {draft && (
        <BottomSheet
          variant='full'
          title={`${isNew ? 'Add' : 'Edit'} ${noun}`}
          onClose={closeSheet}
          footer={(close) => (
            <Button
              variant='primary'
              size='lg'
              isFullWidth
              ariaLabel={isNew ? 'Add' : 'Save'}
              isDisabled={!hasName(draft)}
              onPress={() => {
                commitDraft();
                close();
              }}
            >
              {isNew ? 'Add' : 'Save'}
            </Button>
          )}
        >
          <Stack spacing={2}>
            {cols.map((col) => (
              <TextField
                key={col.key}
                select={col.kind === 'select'}
                label={col.label ?? col.question}
                placeholder={col.placeholder}
                value={asText(draft[col.key])}
                onChange={(e) =>
                  setDraft((d) => ({ ...(d ?? {}), [col.key]: e.target.value }))
                }
                {...textFieldProps}
                slotProps={{ ...textFieldProps.slotProps, select: selectProps }}
              >
                {col.kind === 'select' &&
                  col.options?.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
              </TextField>
            ))}
          </Stack>
        </BottomSheet>
      )}
    </Stack>
  );
};

const Field = ({
  step,
  a,
  set,
}: {
  step: Step;
  a: Answers;
  set: (patch: Answers) => void;
}) => {
  const fieldProps = {
    fullWidth: true,
    slotProps: { inputLabel: { shrink: true } },
  } as const;

  switch (step.kind) {
    case 'name':
      return (
        <Stack spacing={2}>
          <TextField
            label='First name'
            value={asText(a.firstName)}
            onChange={(e) => set({ firstName: e.target.value })}
            {...fieldProps}
          />
          <TextField
            label='Last name'
            value={asText(a.lastName)}
            onChange={(e) => set({ lastName: e.target.value })}
            {...fieldProps}
          />
        </Stack>
      );

    case 'date':
      return (
        <TextField
          type='date'
          label={step.label ?? 'Date of birth'}
          value={asText(a[step.key])}
          onChange={(e) => set({ [step.key]: e.target.value })}
          {...fieldProps}
        />
      );

    case 'text':
      return (
        <TextField
          label={step.label ?? 'Your answer'}
          placeholder={step.placeholder}
          value={asText(a[step.key])}
          onChange={(e) => set({ [step.key]: e.target.value })}
          {...fieldProps}
        />
      );

    case 'currency':
      return (
        <Stack spacing={1.5}>
          <TextField
            label={step.label ?? 'Amount'}
            value={groupDigits(asText(a[step.key]))}
            onChange={(e) => set({ [step.key]: e.target.value })}
            placeholder='0'
            inputMode='numeric'
            {...fieldProps}
            slotProps={{
              ...fieldProps.slotProps,
              input: {
                // The filled label adds top padding; bottom-align the £ so it
                // sits on the value's line rather than centring over the label.
                startAdornment: (
                  <InputAdornment
                    position='start'
                    sx={{ alignSelf: 'flex-end', mt: 0, mb: '8px' }}
                  >
                    £
                  </InputAdornment>
                ),
              },
            }}
          />
          {step.periods && (
            <TextField
              select
              label='How often'
              value={asText(a[`${step.key}Period`]) || step.per || ''}
              onChange={(e) => set({ [`${step.key}Period`]: e.target.value })}
              {...fieldProps}
              slotProps={{ ...fieldProps.slotProps, select: selectProps }}
            >
              {step.periods.map((p) => (
                <MenuItem
                  key={p.value}
                  value={p.value}
                  sx={{ whiteSpace: 'normal' }}
                >
                  Per {p.label.toLowerCase()}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      );

    case 'select':
      return (
        <TextField
          select
          label={step.label ?? 'Select'}
          value={asText(a[step.key])}
          onChange={(e) => set({ [step.key]: e.target.value })}
          {...fieldProps}
          slotProps={{ ...fieldProps.slotProps, select: selectProps }}
        >
          {step.options?.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      );

    case 'choice':
      return (
        <Stack spacing={1.5}>
          {step.options?.map((o) => (
            <ChoiceCard
              key={o.value}
              selected={asText(a[step.key]) === o.value}
              label={o.label}
              onClick={() => set({ [step.key]: o.value })}
            />
          ))}
        </Stack>
      );

    case 'yesno': {
      const options = [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        ...(step.notSure ? [{ value: 'unsure', label: 'Not sure' }] : []),
      ];
      return (
        <Stack spacing={1.5}>
          {options.map((o) => (
            <ChoiceCard
              key={o.value}
              selected={asText(a[step.key]) === o.value}
              label={o.label}
              onClick={() => set({ [step.key]: o.value })}
            />
          ))}
        </Stack>
      );
    }

    case 'multiselect': {
      const selected = asList(a[step.key]);
      // Dropdown: a select whose chosen options show as chips inside the field.
      if (step.asDropdown) {
        return (
          <TextField
            select
            label={step.label ?? 'Select all that apply'}
            value={selected}
            onChange={(e) =>
              set({ [step.key]: e.target.value as unknown as string[] })
            }
            {...fieldProps}
            slotProps={{
              ...fieldProps.slotProps,
              select: {
                ...selectProps,
                multiple: true,
                displayEmpty: true,
                renderValue: (val) => {
                  const vals = val as string[];
                  if (!vals.length) return null;
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {vals.map((v) => (
                        <Chip
                          key={v}
                          label={optionLabel(step, v)}
                          size='small'
                        />
                      ))}
                    </Box>
                  );
                },
              },
            }}
          >
            {step.options?.map((o) => (
              <MenuItem
                key={o.value}
                value={o.value}
                sx={{ whiteSpace: 'normal' }}
              >
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        );
      }
      // Chips: the whole option set, toggled inline.
      const toggle = (v: string) =>
        set({
          [step.key]: selected.includes(v)
            ? selected.filter((s) => s !== v)
            : [...selected, v],
        });
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {step.options?.map((o) => (
            <Chip
              key={o.value}
              label={o.label}
              onClick={() => toggle(o.value)}
              variant={selected.includes(o.value) ? 'filled' : 'outlined'}
              color={selected.includes(o.value) ? 'primary' : 'default'}
            />
          ))}
        </Box>
      );
    }

    case 'table':
      return <RepeatableTable step={step} a={a} set={set} />;
  }
};

// ── Screens ──────────────────────────────────────────────────────────────────

const InfoPoint = ({ icon, text }: { icon: string; text: string }) => (
  <Stack sx={{ alignItems: 'flex-start' }} direction='row' spacing={1.75}>
    <Box
      sx={{
        width: 36,
        height: 36,
        flexShrink: 0,
        borderRadius: '50%',
        bgcolor: 'custom.limeGreen.200',
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon icon={icon as never} fontSize={16} />
    </Box>
    <Typography sx={{ pt: 0.75 }} variant='body1'>
      {text}
    </Typography>
  </Stack>
);

// The intro video, stood in for by its opening frame with a play affordance —
// the real MuxPlayer stays in the shipped flow.
const IntroVideo = () => (
  <Box
    sx={{
      position: 'relative',
      borderRadius: `${radius.md}px`,
      overflow: 'hidden',
      aspectRatio: '16 / 9',
    }}
  >
    <Box
      component='img'
      src='/financial-status-video.png'
      alt='Financial status intro video'
      sx={{
        width: '100%',
        height: '100%',
        display: 'block',
        objectFit: 'cover',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: 56,
        height: 56,
        borderRadius: '50%',
        bgcolor: 'background.default',
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: 'translate(-50%, -50%)',
      }}
    >
      <Icon icon={'play' as never} fontSize={20} />
    </Box>
  </Box>
);

const IntroBody = () => (
  <Stack spacing={3}>
    <PageHeader
      title={INTRO.title}
      description={INTRO.description}
      variant='h2'
    />
    <IntroVideo />
    <Card sx={{ p: 2 }}>
      <Stack spacing={2}>
        {INTRO.points.map((p) => (
          <InfoPoint key={p.text} icon={p.icon} text={p.text} />
        ))}
      </Stack>
    </Card>
    <Box
      sx={{
        p: 2,
        borderRadius: `${radius.md}px`,
        bgcolor: 'custom.limeGreen.100',
        border: 1,
        borderColor: 'custom.neutral.200',
      }}
    >
      <Typography variant='body2' color='text.secondary'>
        {INTRO.footer}
      </Typography>
    </Box>
  </Stack>
);

// A follow-up field disclosed beneath the question when the answer calls for
// it. Its own question sits above the field as a quiet sub-label.
const Reveal = ({
  step,
  a,
  set,
}: {
  step: Step;
  a: Answers;
  set: (patch: Answers) => void;
}) => (
  <Stack spacing={1.5}>
    <Stack spacing={0.5}>
      <Typography variant='subtitle1' color='text.primary'>
        {step.question}
      </Typography>
      {step.helper && (
        <Typography variant='body2' color='text.secondary'>
          {step.helper}
        </Typography>
      )}
    </Stack>
    <Field step={step} a={a} set={set} />
  </Stack>
);

// One section of the review list: an overline + a card of tappable answer rows.
// Each row shows a field's value; tapping it edits the whole question group
// (the parent question plus its conditional fields) in a drawer.
const ReviewSection = ({
  title,
  rows,
  answers,
  onEdit,
}: {
  title: string;
  rows: { step: Step; target: Step }[];
  answers: Answers;
  onEdit: (target: Step) => void;
}) => (
  <Stack spacing={1}>
    <Typography variant='overline' color='text.secondary'>
      {title}
    </Typography>
    <Card sx={{ p: 0, overflow: 'hidden' }}>
      {rows.map(({ step, target }, i) => (
        <Box
          key={step.key}
          onClick={() => onEdit(target)}
          sx={{
            px: 2,
            py: 1.5,
            borderTop: i === 0 ? 0 : 1,
            borderColor: 'custom.neutral.200',
            cursor: 'pointer',
          }}
        >
          <Stack
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
            direction='row'
            spacing={2}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant='body2' color='text.secondary'>
                {step.question}
              </Typography>
              <Typography variant='h6'>
                {formatValue(step, answers) || 'Not answered'}
              </Typography>
            </Box>
            <Icon icon={'pen' as never} fontSize={13} />
          </Stack>
        </Box>
      ))}
    </Card>
  </Stack>
);

const ReviewBody = ({
  steps,
  answers,
  set,
}: {
  steps: Step[];
  answers: Answers;
  set: (patch: Answers) => void;
}) => {
  // The question group being edited from the review list, in a drawer over the
  // summary. Held as a draft so closing without saving leaves answers untouched.
  const [editStep, setEditStep] = useState<Step | null>(null);
  const [draft, setDraft] = useState<Answers>({});
  const draftSet = (patch: Answers) => setDraft((d) => ({ ...d, ...patch }));

  const openEdit = (target: Step) => {
    setEditStep(target);
    setDraft({ ...answers });
  };

  // Reveals to show in the drawer, reactive to edits made there (so toggling
  // e.g. "any allergies?" to Yes surfaces the list immediately).
  const editReveals = editStep
    ? (editStep.reveals ?? []).filter((r) => matches(r, draft))
    : [];

  return (
    <Stack spacing={3}>
      <PageHeader
        title={COMPLETE.title}
        description={COMPLETE.description}
        variant='h2'
      />
      {SECTIONS.map((section) => {
        // A top-level question and its revealed follow-ups each get a row for
        // their value, but all edit the same group (the parent) in the drawer.
        const rows = steps.flatMap((step) =>
          [step, ...(step.reveals ?? []).filter((r) => matches(r, answers))]
            .filter((s) => s.section === section.id)
            .map((s) => ({ step: s, target: step })),
        );
        if (!rows.length) return null;
        return (
          <ReviewSection
            key={section.id}
            title={section.title}
            rows={rows}
            answers={answers}
            onEdit={openEdit}
          />
        );
      })}

      {editStep && (
        <BottomSheet
          variant='hug'
          title={editStep.question}
          onClose={() => setEditStep(null)}
          footer={(close) => (
            <Button
              variant='primary'
              size='lg'
              isFullWidth
              ariaLabel='Save'
              onPress={() => {
                set(draft);
                close();
              }}
            >
              Save
            </Button>
          )}
        >
          <Stack spacing={3}>
            <Field step={editStep} a={draft} set={draftSet} />
            {editReveals.map((r) => (
              <Reveal key={r.key} step={r} a={draft} set={draftSet} />
            ))}
          </Stack>
        </BottomSheet>
      )}
    </Stack>
  );
};

// The body of a single question screen: section label, the question, field —
// plus any progressive-disclosure reveals whose condition the answer now meets.
const QuestionBody = ({
  step,
  a,
  set,
  showSection,
}: {
  step: Step;
  a: Answers;
  set: (patch: Answers) => void;
  showSection: boolean;
}) => {
  const reveals = (step.reveals ?? []).filter((r) => matches(r, a));
  // When answering the question opens follow-ups, bring them to the top of the
  // screen so the next thing to answer is where the eye already is — rather
  // than leaving them below the fold. Skips the case where they were already
  // showing on arrival (this component is keyed per question, so a fresh mount
  // starts from the current count and only a genuine increase scrolls).
  const revealsRef = useRef<HTMLDivElement>(null);
  const prevCount = useRef(reveals.length);
  useEffect(() => {
    if (reveals.length > prevCount.current)
      revealsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    prevCount.current = reveals.length;
  }, [reveals.length]);

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        {showSection && (
          <Typography variant='overline' color='text.secondary'>
            {sectionTitle(step.section)}
          </Typography>
        )}
        <PageHeader
          title={step.question}
          description={step.helper}
          variant='h4'
        />
      </Stack>
      <Field step={step} a={a} set={set} />
      {reveals.length > 0 && (
        <Box ref={revealsRef}>
          <Stack spacing={3}>
            {reveals.map((r) => (
              <Reveal key={r.key} step={r} a={a} set={set} />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

// ── Flow ─────────────────────────────────────────────────────────────────────

export type Phase = 'intro' | 'form' | 'review';

const FinancialAssessment = ({
  onComplete,
  onExit,
  initialPhase = 'intro',
  initialAnswers,
  initialStepKey,
  recipientKnown = true,
  showSection = true,
}: {
  onComplete?: () => void;
  /** Leave the flow entirely — the top "Back" affordance. Falls back to
   *  onComplete when not given. Stepping between questions is "Previous". */
  onExit?: () => void;
  initialPhase?: Phase;
  initialAnswers?: Answers;
  /** Start the form on a specific question (by step key) rather than the first —
   *  lets scenes/handover deep-link to a conditional screen. */
  initialStepKey?: string;
  /** Whether we already hold the care recipient's name. When false, the name
   *  step asks for it (generic copy) instead of confirming a prefilled name. */
  recipientKnown?: boolean;
  /** The section label above each question. Off where the form stands in as
   *  an example of a form, outside the app, and the label is noise. */
  showSection?: boolean;
}) => {
  const [phase, setPhase] = useState<Phase>(initialPhase);
  // Name comes prefilled from her profile; callers can override — or clear it
  // (recipientKnown=false) for the "name not on file yet" entry.
  const [a, setA] = useState<Answers>(
    initialAnswers ?? (recipientKnown ? PREFILL : {}),
  );
  // Start step: the index of initialStepKey within the initially-visible steps,
  // else the first.
  const [step, setStep] = useState(() => {
    const initA = initialAnswers ?? (recipientKnown ? PREFILL : {});
    const vis = STEPS.filter((s) => matches(s, initA));
    const i = vis.findIndex((s) => s.key === initialStepKey);
    return i >= 0 ? i : 0;
  });

  // Steps visible given the current answers — conditional ones drop out.
  const visible = useMemo(() => STEPS.filter((s) => matches(s, a)), [a]);
  const idx = Math.min(step, visible.length - 1);
  const current = visible[idx];

  const set = (patch: Answers) => setA((prev) => ({ ...prev, ...patch }));

  const next = () =>
    idx >= visible.length - 1 ? setPhase('review') : setStep(idx + 1);
  // Step to the prior question — or the intro before the first one.
  const previous = () => {
    if (idx === 0) return setPhase('intro');
    setStep(idx - 1);
  };
  // Leave the flow altogether — the top "Back".
  const exit = () => (onExit ?? onComplete)?.();

  // When we don't hold the name, the name step asks for it rather than
  // confirming one — the rest of the flow is unchanged.
  const displayStep =
    !recipientKnown && current.key === 'name'
      ? {
          ...current,
          question: NAME_UNKNOWN.question,
          helper: NAME_UNKNOWN.helper,
        }
      : current;

  if (phase === 'intro') {
    // Entry screen — Exit leaves the flow; the body carries the big page header.
    return (
      <StackPage
        onBack={exit}
        backLabel='Exit'
        footer={
          <Stack spacing={1.5}>
            <PrimaryAction label={INTRO.cta} onPress={() => setPhase('form')} />
            <Typography
              sx={{ textAlign: 'center' }}
              variant='caption'
              color='text.secondary'
            >
              {INTRO.time}
            </Typography>
          </Stack>
        }
      >
        <IntroBody />
      </StackPage>
    );
  }

  if (phase === 'review') {
    return (
      <StackPage
        onBack={exit}
        backLabel='Exit'
        footer={
          <PrimaryAction label={COMPLETE.cta} onPress={() => onComplete?.()} />
        }
      >
        <ReviewBody steps={visible} answers={a} set={set} />
      </StackPage>
    );
  }

  return (
    <StackPage
      onBack={exit}
      backLabel='Save & exit'
      action={
        <Typography variant='caption' color='text.secondary'>
          {idx + 1}/{visible.length}
        </Typography>
      }
      footer={
        <Stack spacing={2}>
          <Button
            variant='primary'
            size='lg'
            isFullWidth
            ariaLabel={idx >= visible.length - 1 ? 'Review' : 'Next'}
            onPress={next}
          >
            {idx >= visible.length - 1 ? 'Review' : 'Next'}
          </Button>
          <Button
            variant='secondary'
            size='lg'
            isFullWidth
            ariaLabel='Previous question'
            onPress={previous}
          >
            Previous
          </Button>
        </Stack>
      }
    >
      <QuestionBody
        key={displayStep.key}
        step={displayStep}
        a={a}
        set={set}
        showSection={showSection}
      />
    </StackPage>
  );
};

export default FinancialAssessment;
